// START IMPORTS
// Context and configuration imports
import {
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
// Hooks imports
import { usePhoneSearchData, useReport } from '@ltvco/refresh-lib/hooks';
// Theme and UI imports
import { Box, Stack } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  unformatPhone,
  isNeighborSpoofed,
  DateUtil,
  usePhoneFormatter,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  CommentsSection,
  commonTypes,
  DebugMenu,
  NeighborSpoofing,
  NuisanceCaller,
  PhoneFraudScanSection,
  ReportChangesOverview,
  ReportFactory,
  ReportLoading,
  ReportMonitor,
  ReportOptions,
  SearchVolume,
  useRemouladeReportSnapshot,
  useReportMonitors,
  useSession,
  useCreateRemouladeReportSnapshot,
  SerializedPerson,
  useMrRogersPhoneLocation,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  PhoneSection as PhoneSectionV2,
  EmailSection as EmailSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  SocialSection as SocialSectionV2,
  JobsSection as JobsSectionV2,
  EducationSection as EducationSectionV2,
  ReportRating as ReportRatingV2,
  RelatedReportsSection as RelatedReportsSectionV2,
  NotesSection as NotesSectionV2,
  AssetsSection as AssetsSectionV2,
  PotentialOwners as PotentialOwnersV2,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  NavLinkDataV2,
  LizanoListItemDataPhone,
  PhoneReportOverview,
  ReportNullState,
  ReportClaimingSection,
  MonitoringCard,
  useRecentReports,
  ReportActionsWithDateUpdated,
  PhoneFraudScanSection as PhoneFraudScanSectionV2,
  PhoneFraudCommentsSection,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useFeatureIsOn, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import {
  getPhoneNavLinkData,
  getPhoneNavLinkDataV2,
} from 'navLinkData/phoneNavLinkData';
import { isAfterTodaysCutoff } from 'utils/isAfterTodaysCutoff';

// Asset imports
import legitCallerGifUrl from '../../images/caller_legit.gif';
import questionableCallerGifUrl from '../../images/caller_questionable.gif';
import telemarketerCallerGifUrl from '../../images/caller_telemarketer.gif';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PhoneReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'phone';
// END CONSTANTS

export function PhoneReport({ permalink }: PhoneReportProps) {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession();
  const { logError } = useContext(AppConfig);

  // TODO: all this monitored report logic needs a refactor.
  // There is no point in refetching a report when a user toggles monitoring, so we use the initial value.
  const { currentMonitors, reportIsMonitored } = useReportMonitors(permalink);
  const isInStaggeredPhoneUpdateVariation = useFeatureIsOn('OAR-1352');
  const isPhonePartialAddress = useFeatureIsOn('OAR-1706');
  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const hideHighConfidenceSwitch = useFeatureIsOn('RFRSH-2523');
  const isNewFraudSection = useFeatureIsOn('RFRSH-3481');
  const createRemouladeReportSnapshot = useCreateRemouladeReportSnapshot();
  const queryClient = useQueryClient();
  const { isHidden } = useSubscriptionAccessConfigContext();

  const coreResult = useReport(permalink, reportIsMonitored);
  const remouladeResult = useRemouladeReportSnapshot(permalink);
  const flattenedRemouladeResult = remouladeResult
    ? { ...remouladeResult, data: remouladeResult.data?.data }
    : remouladeResult;

  const currentUserInfo = account?.account?.user_info;
  const [spam, setSpam] = useState<'unsure' | 'yes' | 'no'>('unsure');

  const shouldUseRemouladeSnapshot =
    !remouladeResult.isError && reportIsMonitored;
  const queryResult = (
    shouldUseRemouladeSnapshot ? flattenedRemouladeResult : coreResult
  ) as typeof coreResult;

  const zeroed = isZeroed(queryResult);
  const { hash, search } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);

  const {
    data: phoneSearchData,
    error,
    isError,
  } = usePhoneSearchData(queryResult?.data?.meta?.search_data?.phone_number);

  const rawPhone = queryResult?.data?.meta?.search_data?.phone_number ?? '';
  const formattedPhoneNumber = usePhoneFormatter(rawPhone, true);

  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [bbqReportIsReady, setIsBbqReportReady] = useState(false);
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: hideHighConfidenceSwitch ? true : false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };
  const [activeMonitor] = useState(false);
  const [activeClaiming, setActiveClaiming] = useState(false);
  const [neighborSpoofed, setIsNeighborSpoofed] = useState(false);
  const [reportUpdateDate, setUpdateDate] = useState<string | undefined>();
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const { data: bbqPhoneData } = useRecentReports(
    { report_type: 'reverse_phone_report' },
    { enabled: bbqReportIsReady }
  );
  const phoneReports = useMemo(
    () => bbqPhoneData?.reports ?? [],
    [bbqPhoneData?.reports]
  );

  const date = new DateUtil();

  const report = useMemo(() => {
    if (!queryResult.isSuccess || !queryResult.data) return null;

    if (queryResult?.data?.entities)
      return ReportFactory.create(queryResult?.data, 'phone');
  }, [queryResult.data, queryResult.isSuccess]);

  const loading = useMemo(() => {
    return queryResult.isLoading || queryResult.isError;
  }, [queryResult.isLoading, queryResult.isError]);

  const mrRogersPhoneLocation = useMrRogersPhoneLocation(report?.phone || '');
  // END DERIVED_DATA_AND_UTILS

  // START COMPUTED_VALUES
  const hidePersonSection =
    isInStaggeredPhoneUpdateVariation &&
    isAfterTodaysCutoff(currentUserInfo?.join_date) &&
    phoneReports.length == 1;
  // END COMPUTED_VALUES

  // START EFFECTS
  useEffect(() => {
    if (isError && error) {
      const msg = error.error || error.message || '';
      if (!msg.toLowerCase().includes('not found')) {
        logError('Error fetching phone search data', error);
      }
    }
  }, [isError, error, logError]);

  useEffect(() => {
    setActiveClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      queryClient.invalidateQueries(['request', 'bbq']);

      // After a 3 second delay, we expect the report that was just run to be in BBQ
      const timer = setTimeout(() => {
        queryClient.invalidateQueries(['request', 'bbq']);
        setIsBbqReportReady(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [queryResult.data, queryResult.isSuccess, reportOptions]);

  useEffect(() => {
    // Only run the request to remoulade if this report is the user's only phone report, or "first" report
    if (
      !hidePersonSection ||
      phoneReports.length != 1 ||
      report?.data?.people?.length === 0
    )
      return;
    createRemouladeReportSnapshot.mutate({
      permalink,
      update_type: 'phone',
    });
  }, [hidePersonSection, phoneReports]);

  useEffect(() => {
    const remoUpdateDate = currentMonitors?.find(
      (monitor) => monitor.permalink === permalink
    )?.updated_at;
    const coreUpdateDate = report?.data?.meta?.updated_at;

    if (reportIsMonitored && remoUpdateDate)
      setUpdateDate(date.parseDateFromString(remoUpdateDate, 'yyyy-MM-dd'));
    else if (coreUpdateDate)
      setUpdateDate(
        date.parseDateFromString(
          coreUpdateDate,
          'yyyy-MM-dd',
          'yyyy-MM-dd HH:mm:ss ZZZ'
        )
      );

    if (report) {
      const spoofed = isNeighborSpoofed(
        report?.data,
        account?.account?.user_info.phone_number
      );
      setIsNeighborSpoofed(spoofed);
    }
  }, [
    report?.data?.meta?.updated_at,
    currentMonitors,
    permalink,
    reportIsMonitored,
  ]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (loading || queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={15} />;
  }
  if (zeroed || (queryResult.isSuccess && !report)) {
    return <ReportNullState />;
  }

  if (!report) return <ReportLoading menuItems={15} />;

  if (!report?.data?.people && !report?.data?.people?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const showReportClaiming =
    unformatPhone(currentUserInfo?.phone_number || '') === report?.phone;

  const ClaimingComponent = showReportClaiming && (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  );

  const MonitoringComponent = (
    <MonitoringCard permalink={permalink} reportType={reportType} />
  );

  const owner = report.getOwner(reportOptions);

  const {
    ownerName,
    emails,
    educations,
    jobs,
    profiles,
    usernames,
    phones,
    addresses,
    ownedAssets,
    relatives,
  } = owner;

  const { risk, connectivity, is_valid } = report.supportingPhone || {};
  const {
    fraud_score,
    is_prepaid,
    is_voip,
    is_risky,
    is_recent_abuse,
    is_do_not_call,
    is_spammer,
  } = risk || {};

  const is_active = connectivity?.is_active;

  const showPhoneFraudScan =
    report.supportingPhone != null && risk != null && connectivity != null;

  const queryParams = new URLSearchParams(search);
  const fallbackPhone = queryParams.get('phone') || '';

  const otherPhones = phones.filter(
    (phone: commonTypes.Phone) =>
      phone.number !== fallbackPhone && phone.number !== report.phone
  );

  const business = report?.data?.rawData?.entities?.businesses[0];
  const businessName = business?.names[0]?.name ?? '';

  let potentialOwnersNumber = 0;

  if (report.data.people.length > 0)
    potentialOwnersNumber = report.data.people.length;
  else if (businessName) potentialOwnersNumber = 1;
  if (isInListV2Variation) potentialOwnersNumber = 1;

  const { main_line_type, num_searches, spam_score } = report.data;
  const searchCount = phoneSearchData?.total_volume || num_searches || 0;

  const phoneNavLinkDataV2 = getPhoneNavLinkDataV2(owner, {
    potentialOwnersLength: potentialOwnersNumber,
    searchVolumeLength: searchCount > 0 ? 1 : 0,
    commentsLength: report.data.comments?.length ?? 0,
    neighborSpoofingLength: neighborSpoofed ? 1 : 0,
    spamReportsCount: report.data.spam_reports_count ?? 0,
  });

  const phoneNavLinkData = getPhoneNavLinkData(owner, {
    potentialOwnersLength: potentialOwnersNumber,
    searchVolumeLength: searchCount > 0 ? 1 : 0,
    commentsLength: report.data.comments?.length ?? 0,
    neighborSpoofingLength: neighborSpoofed ? 1 : 0,
    spamReportsCount: report.data.spam_reports_count ?? 0,
  });

  const comments = report.data.comments || [];
  const carrier = report.data.meta?.search_data?.carrier;

  document.title = `${formattedPhoneNumber} - BeenVerified`;

  const buildReportDataForListV2 = () => {
    const data: LizanoListItemDataPhone = {
      permalink,
      phone_number: report?.phone || '',
      type: main_line_type || '',
      carrier: carrier || '',
      area_code_location: mrRogersPhoneLocation[1] || '',
    };

    if (report?.data?.people && report?.data?.people?.length) {
      data.owners = report.data.people.map((owner: SerializedPerson) => {
        return {
          first_name: owner?.identity?.names?.[0]?.parsed?.first || '',
          last_name: owner?.identity?.names?.[0]?.parsed?.last || '',
        };
      });
    }

    return data;
  };

  const potentialOwners = report.data.people;

  const alternativeTitle = isPhonePartialAddress
    ? 'Possible Locations'
    : undefined;

  const internationalPhoneNumber = (rawPhone as string).startsWith('+');
  const supportingPhone = queryResult?.data?.supporting?.phones?.[0];

  const fallbackInfo = internationalPhoneNumber
    ? {
        address: supportingPhone?.location?.address || undefined,
        phone: formattedPhoneNumber || undefined,
      }
    : undefined;

  if (internationalPhoneNumber) {
    phoneNavLinkDataV2.forEach((item) => {
      if (item.childItems) {
        item.childItems = item.childItems.filter(
          (child) => child.href !== '#related-reports-section'
        );
      }
    });
  }
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        {/* START DESKTOP_NAVIGATION */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Phone Report"
            headerTitle={formattedPhoneNumber}
            menuItems={
              phoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        {/* END DESKTOP_NAVIGATION */}

        {/* START MAIN_CONTENT */}
        <Box marginTop={0}>
          {/* START REPORT_CHANGES_OVERVIEW */}
          {activeMonitor && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={phoneNavLinkData}
            />
          )}
          {/* END REPORT_CHANGES_OVERVIEW */}

          {/* START REPORT_ACTIONS */}
          <Box
            sx={{
              height: 30,
              backgroundColor: '#f8f8f8',
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                sm: 'block',
                md: 'block',
                lg: 'block',
              },
            }}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Phone"
            dateUpdated={reportUpdateDate}
            permalink={permalink}
            navLinkData={phoneNavLinkDataV2 as unknown as NavLinkDataV2[]}
            reportUpgraded={false}
            reportDataForListV2={
              isInListV2Variation ? buildReportDataForListV2() : undefined
            }
          />
          {/* END REPORT_ACTIONS */}

          {/* START REPORT_OVERVIEW */}

          <PhoneReportOverview
            dateUpdated={reportUpdateDate}
            reportType={reportType}
            phone={formattedPhoneNumber}
            personName={ownerName || businessName}
            neighborSpoofed={neighborSpoofed}
            phoneOverviewItemsData={{
              comments,
              mainLineType: main_line_type,
              numSearches: searchCount,
              spamScore: spam_score,
              carrier,
              peopleCount: potentialOwnersNumber,
            }}
            Monitoring={MonitoringComponent}
            showHighConfidenceToggle={!hideHighConfidenceSwitch}
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) => {
              handleSetReportOptions({ showHighConfidenceDataOnly: value });
            }}
          />
          {/* END REPORT_OVERVIEW */}

          {ClaimingComponent}

          {/* START PERSONAL_INFO_SECTIONS */}
          <>
            <PotentialOwnersV2
              potentialOwners={potentialOwners || []}
              potentialOwnerIndex={reportOptions.potentialOwnerIndex}
              setPotentialOwnerIndex={(index: number) => {
                handleSetReportOptions({ potentialOwnerIndex: index });
              }}
              personName={ownerName || formattedPhoneNumber}
              permalink={permalink}
              business={business}
              showHighConfidenceDataOnly={
                reportOptions.showHighConfidenceDataOnly
              }
              setShowHighConfidenceDataOnly={(value: boolean) => {
                handleSetReportOptions({
                  showHighConfidenceDataOnly: value,
                });
              }}
              hidePersonSection={hidePersonSection}
              reportType={reportType}
              hideOpenReportCTA={internationalPhoneNumber}
              fallbackInfo={fallbackInfo}
            />
            <PhoneSectionV2
              personName={ownerName || formattedPhoneNumber}
              phoneList={otherPhones}
              permalink={permalink}
              alternateTitle="Other Phone Numbers"
            />
            <EmailSectionV2
              personName={ownerName || formattedPhoneNumber}
              emailList={emails}
              permalink={permalink}
              openEmailReportCTA={!internationalPhoneNumber}
            />
            <AddressHistorySectionV2
              addresses={addresses}
              personName={ownerName || formattedPhoneNumber}
              permalink={permalink}
              alternateTitle={alternativeTitle}
              openReportCTA={!internationalPhoneNumber}
              enableCardClick={!internationalPhoneNumber}
            />
            <SocialSectionV2
              personName={ownerName || formattedPhoneNumber}
              profiles={profiles}
              usernames={usernames}
              permalink={permalink}
              openSocialReportCTA={!internationalPhoneNumber}
            />
            <JobsSectionV2
              personName={ownerName || formattedPhoneNumber}
              jobs={jobs}
              permalink={permalink}
            />
            <EducationSectionV2
              personName={ownerName || formattedPhoneNumber}
              educations={educations}
              permalink={permalink}
            />
            <AssetsSectionV2
              personName={ownerName || formattedPhoneNumber}
              ownedAssets={ownedAssets}
              permalink={permalink}
            />
          </>
          {/* END PERSONAL_INFO_SECTIONS */}

          {/* START FRAUD_SCAN_SECTION */}
          {isNewFraudSection ? (
            <PhoneFraudScanSectionV2
              is_risky={is_risky}
              is_do_not_call={is_do_not_call}
              is_voip={is_voip}
              is_prepaid={is_prepaid}
              is_valid={is_valid}
              is_recent_abuse={is_recent_abuse}
              phoneNumber={formattedPhoneNumber}
              showPhoneFraudScan={showPhoneFraudScan}
              showReportCTA={!isHidden('fraud_scan')}
              is_spammer={is_spammer}
            />
          ) : (
            <PhoneFraudScanSection
              fraudScore={fraud_score}
              validStatus={is_valid}
              prepaid={is_prepaid}
              voip={is_voip}
              risky={is_risky}
              recentAbuse={is_recent_abuse}
              active={is_active}
              doNotCall={is_do_not_call}
              phoneNumber={formattedPhoneNumber}
              showPhoneFraudScan={showPhoneFraudScan}
              showReportCTA={!isHidden('fraud_scan')}
            />
          )}
          {/* END FRAUD_SCAN_SECTION */}

          {/* START ADDITIONAL_INFO_SECTIONS */}
          <NeighborSpoofing phoneReportData={report.data} />
          <SearchVolume searchCount={searchCount} />
          <NuisanceCaller
            reportData={report.data}
            callerSourceMap={{
              telemarketer: telemarketerCallerGifUrl,
              questionable: questionableCallerGifUrl,
              legit: legitCallerGifUrl,
            }}
          />
          {isNewFraudSection ? (
            <PhoneFraudCommentsSection
              comments={comments}
              formatted_number={formattedPhoneNumber}
              permalink={permalink}
              spam={spam}
              setSpam={setSpam}
            />
          ) : (
            <CommentsSection reportData={report.data} permalink={permalink} />
          )}

          <NotesSectionV2 permalink={permalink} />

          {!internationalPhoneNumber && (
            <RelatedReportsSectionV2
              reportType="phone"
              personName={ownerName || formattedPhoneNumber}
              potentialOwners={report.data.people}
              relatives={relatives}
              addresses={addresses}
              phones={phones}
              emails={emails}
              usernames={usernames}
              hidePersonSection={hidePersonSection}
            />
          )}

          <ReportRatingV2
            rating={report.data.meta?.rating || null}
            report_type={reportType}
          />
          {/* END ADDITIONAL_INFO_SECTIONS */}
        </Box>
        {/* END MAIN_CONTENT */}
      </Stack>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Phone Report"
        headerTitle={formattedPhoneNumber}
        menuItems={phoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']}
      />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </Box>
  );
  // END RENDER
}
